import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Chip, EndpointCard } from "./EndpointCard";
import { ReconciliationSection } from "./ReconciliationSection";
import { DiagnosticsSection } from "./DiagnosticsSection";
import { FundsAuditSection } from "./FundsAuditSection";
import { utc } from "./syncText";

const KINDS = [["funds", "Funds & margins"], ["holdings", "Holdings"], ["positions", "Positions"]];
const newKey = () => `sync-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function useConnection() {
  return useQuery({
    queryKey: ["sharekhan-status"],
    queryFn: async () => (await api.get("/brokers/sharekhan/status")).data,
    retry: false,
  });
}

function ConnectionLine({ conn, live }) {
  const text = !live ? "Live mode OFF — sync unavailable (history stays readable)"
    : conn?.connected ? `Linked (read-only) until ${utc(conn.expires_at)}` : "Not linked — sign in through the login window below to sync";
  return <span data-testid="sync-connection" className={conn?.connected && live ? "text-emerald-400" : "text-slate-400"}>{text}</span>;
}

function SyncButton({ enabled }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: async () => (await api.post("/brokers/sharekhan/sync", null, { headers: { "Idempotency-Key": newKey() } })).data,
    onSuccess: (r) => { toast[r.status === "completed" ? "success" : "warning"](`Sync ${r.status}; session disconnected`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
    onSettled: () => ["sharekhan-status", "sharekhan-sync-status", "sharekhan-real-recon", "sharekhan-readiness", "sharekhan-diagnostics"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
  return (
    <Button size="sm" data-testid="sync-run-btn" disabled={!enabled || m.isPending} onClick={() => m.mutate()}
      title={enabled ? "" : "Available only during an authorized Sharekhan session"}
      className="bg-blue-600 hover:bg-blue-500 text-white disabled:bg-slate-700 disabled:text-slate-400 disabled:opacity-100">
      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${m.isPending ? "animate-spin" : ""}`} /> {m.isPending ? "Syncing…" : "Sync now (read-only, then disconnect)"}
    </Button>
  );
}

function Summary({ s }) {
  const last = s.last_run;
  const warn = last && last.status !== "completed";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
      <span>Last successful sync: <span data-testid="sync-last-success" className="font-mono text-slate-300">{utc(s.last_successful_run?.finished_at)}</span></span>
      <span className="flex items-center gap-1">Last run: {last ? <Chip status={last.status} testId="sync-last-status" /> : <span data-testid="sync-last-status">none</span>}</span>
      {s.sync_in_progress && <span data-testid="sync-in-progress" className="text-sky-300">Sync in progress…</span>}
      {warn && (
        <span data-testid="sync-action-required" className="flex items-center gap-1 text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5" /> Action required: last sync {last.status}{last.session_rejected ? " (session expired — sign in again)" : ""}
        </span>
      )}
    </div>
  );
}

export function SharekhanSyncPanel() {
  const conn = useConnection();
  const { data: s, isLoading, isError, refetch } = useQuery({
    queryKey: ["sharekhan-sync-status"],
    queryFn: async () => (await api.get("/brokers/sharekhan/sync/status")).data,
  });
  const live = !!s?.live_enabled;
  return (
    <Panel title="Sharekhan · Account sync & reconciliation (read-only)" testId="sharekhan-sync-panel" actions={<ShieldCheck className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-4 text-xs">
        {isLoading && <div data-testid="sync-loading" className="text-slate-500">Loading sync status…</div>}
        {isError && <div data-testid="sync-error" className="text-rose-300">Sync status could not be loaded. <button className="underline" onClick={() => refetch()}>Retry</button></div>}
        {s && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <ConnectionLine conn={conn.data} live={live} />
              <SyncButton enabled={live && !!conn.data?.connected && !s.sync_in_progress} />
            </div>
            <Summary s={s} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {KINDS.map(([k, label]) => <EndpointCard key={k} kind={k} label={label} ep={s.last_run?.endpoints?.[k]} snap={s.snapshots?.[k]} />)}
            </div>
            <DiagnosticsSection />
            <FundsAuditSection />
            <ReconciliationSection />
            <div data-testid="sync-safety-note" className="text-[10px] text-slate-500">Values are stored encrypted and shown only to you. Sync never changes the ledger, holdings or capital, and order placement is impossible. Orders possible: no.</div>
          </>
        )}
      </div>
    </Panel>
  );
}
